import { useEffect, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Collapsible,
  Form,
  InlineStack,
  Link,
  Page,
  Text,
  TextField,
} from "@shopify/polaris";
import { SearchIcon, ClipboardIcon } from "@shopify/polaris-icons";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { formatPrice, formatDate, formatPlate } from "../../utility/funcs";
import { POSITION_LABEL } from "./PositionDiagram";

// "¿Toca?": before authorizing work a shop proposes, check each job against
// the car's own history (service_precheck RPC) — so Perla can answer on the
// spot "no, las balatas se cambiaron hace 7,000 km, y las cambiaste tú".

const POSITIONS = {
  corner: ["FL", "FR", "RL", "RR"],
  axle: ["front_axle", "rear_axle"],
  wheel: ["FL", "FR", "RL_outer", "RL_inner", "RR_outer", "RR_inner"],
};

const VERDICT = {
  too_soon: { tone: "critical", label: "No toca", icon: "❌" },
  due: { tone: "success", label: "Toca", icon: "✅" },
  first: { tone: "info", label: "Primera vez en esta unidad", icon: "🆕" },
  no_interval: { tone: undefined, label: "Sin regla de intervalo", icon: "ℹ️" },
};

const km = (n) => (n == null ? "?" : `${Number(n).toLocaleString("es-MX")} km`);

function intervalText(i) {
  if (!i.interval) return null;
  if (i.interval.km)
    return i.interval.mode === "schedule"
      ? `Intervalo ${km(i.interval.km)}`
      : `Vida mínima ${km(i.interval.km)}`;
  if (i.interval.months) return `Vida mínima ${i.interval.months} meses`;
  return null;
}

function lastText(i) {
  const l = i.last;
  if (!l) return "No hay registro de este trabajo en la unidad.";
  const since =
    i.km_since != null && i.km_since >= 0
      ? `hace ${km(i.km_since)}`
      : i.months_since != null
        ? `hace ${i.months_since} meses`
        : "";
  return `Última vez ${formatDate(l.date)} a ${km(l.odometer)} por ${l.provider || "?"}, ${formatPrice(l.price)} (#${
    l.service_id
  })${since ? ` · ${since}` : ""}.`;
}

// Text Perla can paste to the shop on WhatsApp.
function summaryText(res) {
  const lines = res.items.map((i) => {
    const v = VERDICT[i.verdict] || VERDICT.no_interval;
    const pos = i.positions?.length ? ` (${i.positions.map((p) => POSITION_LABEL[p] || p).join(", ")})` : "";
    if (i.verdict === "too_soon")
      return `${v.icon} *${i.type_name}${pos}*: ya se hizo el ${formatDate(i.last.date)} a ${km(i.last.odometer)}${
        i.last.provider ? ` (${i.last.provider})` : ""
      }; ${intervalText(i)?.toLowerCase() || ""}. No autorizado.`;
    if (i.verdict === "due")
      return `${v.icon} *${i.type_name}${pos}*: autorizado${i.km_since != null ? ` (${km(i.km_since)} desde el último)` : ""}.`;
    if (i.verdict === "first") return `${v.icon} *${i.type_name}${pos}*: sin registro previo; enviar foto de la pieza a cambiar.`;
    return `${v.icon} *${i.type_name}${pos}*: ${i.last ? `último el ${formatDate(i.last.date)} a ${km(i.last.odometer)}` : "sin registro"}.`;
  });
  return `Revisión de la unidad *${res.car.model} ${formatPlate(res.car.plate)}* (${km(res.odometer_used)}):\n\n${lines.join("\n")}`;
}

function ResultCard({ item }) {
  const [open, setOpen] = useState(false);
  const v = VERDICT[item.verdict] || VERDICT.no_interval;
  const p = item.price;
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center">
          <Text variant="headingSm" as="h3">
            {item.type_name}
            {item.positions?.length ? ` · ${item.positions.map((x) => POSITION_LABEL[x] || x).join(", ")}` : ""}
          </Text>
          <Badge tone={v.tone}>{v.label}</Badge>
        </InlineStack>
        <Text>{lastText(item)}</Text>
        {intervalText(item) && (
          <Text tone="subdued" variant="bodySm">
            {intervalText(item)}
          </Text>
        )}
        {p?.median && (
          <Text tone="subdued" variant="bodySm">
            {`Precio normal${item.per_piece ? " por pieza" : ""} ${p.basis === "model" ? "para este modelo" : "en la flotilla"}: ${formatPrice(
              p.median,
            )} sin IVA (rango ${formatPrice(p.p10)} – ${formatPrice(p.p90)}, ${p.n} servicios)`}
          </Text>
        )}
        {item.history?.length > 1 && (
          <BlockStack gap="100">
            <InlineStack>
              <Button variant="plain" disclosure={open ? "up" : "down"} onClick={() => setOpen((x) => !x)}>
                {`Historial (${item.history.length})`}
              </Button>
            </InlineStack>
            <Collapsible open={open} id={`hist-${item.type_id}`} transition={false}>
              <BlockStack gap="100">
                {item.history.map((h) => (
                  <Text key={h.service_id} variant="bodySm">
                    <Link url={`/services/${h.service_id}`} target="_blank">{`#${h.service_id}`}</Link>
                    {` · ${formatDate(h.date)} · ${km(h.odometer)} · ${h.provider || "?"} · ${formatPrice(h.price)}`}
                    {h.positions?.length ? ` · ${h.positions.map((x) => POSITION_LABEL[x] || x).join(", ")}` : ""}
                  </Text>
                ))}
              </BlockStack>
            </Collapsible>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

function ServicePrecheck() {
  const [plate, setPlate] = useState("");
  const [car, setCar] = useState(null);
  const [searching, setSearching] = useState(false);
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState({}); // typeId -> positions[] (or [])
  const [odo, setOdo] = useState("");
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    supabase
      .from("service_types")
      .select("id, name, position_kind")
      .eq("preventive", true)
      .order("name")
      .then(({ data }) => setTypes(data || []));
  }, []);

  const findCar = async () => {
    const q = plate.trim().toUpperCase();
    if (!q) return;
    setSearching(true);
    setError(null);
    setResult(null);
    const { data, error: err } = await supabase
      .from("cars")
      .select("id, plate, vin, year, model(name, brand(name))")
      .or(`plate.eq.${q},vin.eq.${q}`)
      .limit(1)
      .maybeSingle();
    setSearching(false);
    if (err) setError(err.message);
    else if (!data) setError("No se encontró la unidad.");
    setCar(data || null);
    setOdo("");
  };

  const toggleType = (t) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (t.id in next) delete next[t.id];
      else next[t.id] = [];
      return next;
    });
  const togglePos = (typeId, pos) =>
    setSelected((prev) => {
      const cur = prev[typeId] || [];
      return { ...prev, [typeId]: cur.includes(pos) ? cur.filter((p) => p !== pos) : [...cur, pos] };
    });

  const check = async () => {
    setChecking(true);
    setError(null);
    const ids = Object.keys(selected).map(Number);
    const positions = Object.fromEntries(
      Object.entries(selected).filter(([, v]) => v.length > 0),
    );
    const { data, error: err } = await supabase.rpc("service_precheck", {
      p_car: car.id,
      p_types: ids,
      p_positions: positions,
      p_odometer: odo === "" ? null : Math.round(Number(odo)),
    });
    setChecking(false);
    if (err || data?.error) {
      setError(err?.message || data.error);
      return;
    }
    setResult(data);
    if (odo === "" && data.odometer_used != null) setOdo(String(data.odometer_used));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText(result));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const nSelected = Object.keys(selected).length;

  return (
    <AppWrapper>
      <Page
        title="¿Toca servicio?"
        subtitle="Revisa lo que propone el taller contra el historial de la unidad antes de autorizar"
      >
        <BlockStack gap="300">
          {error && <Banner tone="critical">{error}</Banner>}

          <Card>
            <BlockStack gap="300">
              {/* A form so Enter / the phone keyboard's "Ir" searches. */}
              <Form onSubmit={findCar}>
                <InlineStack gap="200" blockAlign="end" wrap={false}>
                  <div style={{ flex: 1 }}>
                    <TextField label="Placa o VIN" value={plate} onChange={setPlate} autoComplete="off" />
                  </div>
                  <Button icon={SearchIcon} loading={searching} submit>
                    Buscar
                  </Button>
                </InlineStack>
              </Form>
              {car && (
                <Text fontWeight="semibold">
                  {`${car.model?.brand?.name || ""} ${car.model?.name || ""} ${car.year || ""} · ${car.plate}`}
                </Text>
              )}
            </BlockStack>
          </Card>

          {car && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm" as="h3">
                  ¿Qué propone el taller?
                </Text>
                <InlineStack gap="200">
                  {types.map((t) => (
                    <Button key={t.id} pressed={t.id in selected} onClick={() => toggleType(t)}>
                      {t.name}
                    </Button>
                  ))}
                </InlineStack>
                {types
                  .filter((t) => t.id in selected && POSITIONS[t.position_kind])
                  .map((t) => (
                    <BlockStack key={t.id} gap="100">
                      <Text tone="subdued" variant="bodySm">
                        {`${t.name}: ¿qué posiciones? (opcional)`}
                      </Text>
                      <InlineStack gap="200">
                        {POSITIONS[t.position_kind].map((p) => (
                          <Button
                            key={p}
                            size="slim"
                            pressed={selected[t.id]?.includes(p)}
                            onClick={() => togglePos(t.id, p)}
                          >
                            {POSITION_LABEL[p] || p}
                          </Button>
                        ))}
                      </InlineStack>
                    </BlockStack>
                  ))}
                <InlineStack gap="200" blockAlign="end" wrap={false}>
                  <div style={{ width: 180 }}>
                    <TextField
                      label="Km actuales"
                      type="number"
                      value={odo}
                      onChange={setOdo}
                      autoComplete="off"
                      placeholder="Último registrado"
                      helpText="Del tablero, si lo tienes"
                    />
                  </div>
                  <Button variant="primary" disabled={nSelected === 0} loading={checking} onClick={check}>
                    Revisar
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          )}

          {result && (
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text tone="subdued">{`Calculado con ${km(result.odometer_used)}`}</Text>
                <Button icon={ClipboardIcon} onClick={copy}>
                  {copied ? "¡Copiado!" : "Copiar para WhatsApp"}
                </Button>
              </InlineStack>
              {result.items.map((i) => (
                <ResultCard key={i.type_id} item={i} />
              ))}
            </BlockStack>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default ServicePrecheck;
